import { t, getLocale, setLocale, useLocale } from './i18n.js';
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ShieldCheck, Mail, KeyRound, Copy, Download } from 'lucide-react';
import { api, send, setCsrf } from './api.js';
import { Logo, Notice, Loading } from './components.js';
import { normalizeSsoDomain } from '../shared/sso-domain.js';
const ssoAttemptKey = 'vename-mail-sso-attempt';

function returnedSsoInput() {
  const params = new URLSearchParams(location.search);
  if (params.has('sso_error')) {
    try {
      const previous = sessionStorage.getItem(ssoAttemptKey);
      if (previous) return previous;
    } catch {
      // Private browsing can deny session storage; keep the domain returned by the server.
    }
  }
  return params.get('sso_domain') ?? '';
}

export function Login({ onSuccess, initial }: { onSuccess: () => void; initial?: any }) {
  useLocale();
  const ssoInput = useRef<HTMLInputElement>(null);
  const ssoDetails = useRef<HTMLDetailsElement>(null);
  const [stage, setStage] = useState(initial?.mfaRequired ? 'mfa' : 'login'),
    [email, setEmail] = useState(''),
    [ssoDomain, setSsoDomain] = useState(returnedSsoInput),
    [ssoError, setSsoError] = useState(
      () => new URLSearchParams(location.search).get('sso_error') ?? '',
    ),
    [password, setPassword] = useState(''),
    [code, setCode] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(() => {
      const returned = new URLSearchParams(location.hash.slice(1)).get('auth');
      if (returned === 'mfa-required')
        return t(
          'La double authentification doit être confirmée. Si vous venez de configurer votre application d’authentification, relancez la connexion et saisissez son code.',
        );
      if (returned === 'expired') return t('La connexion a expiré avant d’aboutir. Relancez-la.');
      return '';
    }),
    [config, setConfig] = useState<any>(),
    [session, setSession] = useState(initial),
    [setup, setSetup] = useState<any>(),
    [recovery, setRecovery] = useState<string[]>([]),
    [saved, setSaved] = useState(false),
    [copied, setCopied] = useState(false);
  const invitation = new URLSearchParams(location.hash.slice(1)).get('invitation');
  const ssoErrorText =
    ssoError === 'invalid'
      ? t('Indiquez un domaine ou une adresse professionnelle valide.')
      : ssoError === 'unconfigured'
        ? t(
            'Aucune connexion Microsoft n’est configurée pour ce domaine. Vérifiez-le ou contactez votre administrateur.',
          )
        : ssoError === 'throttled'
          ? t('Trop de tentatives. Réessayez dans quelques minutes.')
          : ssoError
            ? t('La connexion Microsoft est momentanément indisponible. Réessayez.')
            : '';
  useEffect(() => {
    try {
      sessionStorage.removeItem(ssoAttemptKey);
    } catch {
      // The SSO form still works if session storage is unavailable.
    }
    if (new URLSearchParams(location.search).has('sso_error')) {
      const url = new URL(location.href);
      url.searchParams.delete('sso_error');
      url.searchParams.delete('sso_domain');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
    if (new URLSearchParams(location.hash.slice(1)).has('auth'))
      history.replaceState(null, '', location.pathname + location.search);
    api('/auth/config')
      .then((c) => {
        setConfig(c);
        // Only the local demonstration offers an account to prefill; production starts empty.
        setEmail((current) => current || c.localAccounts?.[0] || '');
      })
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    if (config?.oidc && ssoError) {
      ssoDetails.current?.setAttribute('open', '');
      ssoInput.current?.focus();
    }
  }, [config?.oidc, ssoError]);
  const startSso = () => {
    const domain = normalizeSsoDomain(ssoDomain);
    if (!domain) {
      setSsoError('invalid');
      ssoInput.current?.focus();
      return;
    }
    setSsoError('');
    try {
      sessionStorage.setItem(ssoAttemptKey, ssoDomain.trim());
    } catch {
      // The server's normalized domain remains the fallback on return.
    }
    location.assign(
      `/v1/auth/entra/start?domain=${encodeURIComponent(domain)}&locale=${getLocale()}`,
    );
  };
  useEffect(() => {
    if (stage === 'mfa' && session?.enrollmentRequired)
      send('/auth/mfa/enroll', {})
        .then(setSetup)
        .catch((e) => setError(e.message));
  }, [stage, session]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (stage === 'login') {
        const s = invitation
          ? await send('/auth/invitation', { token: invitation, password })
          : await send('/auth/login', { email, password });
        setCsrf(s.csrfToken);
        setSession(s);
        setStage('mfa');
        setPassword('');
        if (invitation) history.replaceState(null, '', location.pathname);
      } else {
        const result = await send('/auth/mfa/verify', { code });
        if (result.recoveryCodes?.length) {
          setRecovery(result.recoveryCodes);
          setStage('recovery');
        } else onSuccess();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(recovery.join('\n'));
      setCopied(true);
    } catch {
      setError(t('Sélectionnez les codes pour les copier manuellement.'));
    }
  };
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob(
        [
          `Codes de secours Vename Mail — ${email}\nConservez-les hors de votre messagerie. Chaque code ne fonctionne qu’une fois.\n\n${recovery.join('\n')}`,
        ],
        { type: 'text/plain' },
      ),
    );
    a.download = 'vmail-codes-de-secours.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="login-page">
      <section className="login-story">
        <Logo onDark />
        <div className="story-content">
          <span className="eyebrow">{t('VOTRE TRAVAIL. VOS ÉCHANGES.')}</span>
          <h1>
            {t('Les bonnes idées')}
            <br />
            {t('commencent par')}
            <br />
            <span>{t('un message.')}</span>
          </h1>
          <p>
            {t('Un espace clair pour échanger, avancer ensemble')}
            <br className="desktop-only" /> {t('et garder l’essentiel à portée de main.')}
          </p>
          <div className="story-illustration" aria-hidden="true">
            <div className="floating-envelope">
              <Mail size={60} strokeWidth={1} />
              <span className="envelope-dot" />
            </div>
            <div className="orbit one" />
            <div className="orbit two" />
            <div className="float-badge">
              <ShieldCheck size={20} /> {t('Votre espace, protégé.')}
            </div>
          </div>
        </div>
        <footer>
          {t('Une messagerie pensée pour vous.')}
          <span>{t('Par Vename')}</span>
        </footer>
      </section>
      <main className="login-form-side">
        <div className="mobile-brand">
          <Logo />
        </div>
        <div className="login-form">
          <div className="language-switch" role="group" aria-label={t('Langue')}>
            {(['fr', 'en'] as const).map((l) => (
              <button
                key={l}
                type="button"
                className={getLocale() === l ? 'active' : ''}
                aria-pressed={getLocale() === l}
                onClick={() => setLocale(l)}
              >
                {l === 'fr' ? 'Français' : 'English'}
              </button>
            ))}
          </div>
          <span className="step-pill">
            {stage === 'login'
              ? t('Bienvenue chez vous')
              : stage === 'mfa'
                ? t('Un accès bien protégé')
                : t('Dernière étape')}
          </span>
          <h2>
            {stage === 'login'
              ? invitation
                ? t('Votre invitation Vename Mail')
                : t('Heureux de vous retrouver.')
              : stage === 'mfa'
                ? session?.enrollmentRequired
                  ? t('Protégez votre compte.')
                  : t('Vérifiez votre identité.')
                : t('Gardez vos codes de secours.')}
          </h2>
          <p className="muted">
            {stage === 'login'
              ? invitation
                ? t('Définissez un mot de passe pour activer votre compte.')
                : t('Connectez-vous à votre messagerie professionnelle.')
              : stage === 'mfa'
                ? session?.enrollmentRequired
                  ? t(
                      t(
                        'Scannez ce QR code avec votre application d’authentification, puis saisissez le code à six chiffres.',
                      ),
                    )
                  : t(
                      t(
                        'Saisissez le code de votre application d’authentification ou un code de secours.',
                      ),
                    )
                : t(
                    t(
                      'Ils vous permettront de vous connecter si vous perdez votre téléphone. Chaque code est utilisable une seule fois.',
                    ),
                  )}
          </p>
          {error && <Notice type="error">{error}</Notice>}
          {stage === 'recovery' ? (
            <>
              <div className="recovery-codes">
                {recovery.map((c) => (
                  <code key={c}>{c}</code>
                ))}
              </div>
              <div className="button-row">
                <button className="secondary" onClick={copy}>
                  <Copy size={16} />
                  {copied ? t('Copiés') : t('Copier')}
                </button>
                <button className="secondary" onClick={download}>
                  <Download size={16} />
                  {t('Télécharger')}
                </button>
              </div>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={saved}
                  onChange={(e) => setSaved(e.target.checked)}
                />
                {t('J’ai conservé mes codes dans un endroit sûr.')}
              </label>
              <button className="primary full" disabled={!saved} onClick={onSuccess}>
                {t('Ouvrir ma messagerie')}
                <ArrowRight size={18} />
              </button>
            </>
          ) : (
            <form onSubmit={submit}>
              {stage === 'login' ? (
                <>
                  {!invitation && !config?.oidc && (
                    <label>
                      {t('Adresse professionnelle')}
                      <div className="input-icon">
                        <Mail size={18} />
                        <input
                          type="email"
                          autoComplete="username"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          autoFocus
                          placeholder="vous@entreprise.com"
                        />
                      </div>
                    </label>
                  )}
                  {!config?.oidc && (
                    <label>
                      {invitation ? t('Nouveau mot de passe') : t('Mot de passe')}
                      <div className="input-icon">
                        <KeyRound size={18} />
                        <input
                          type="password"
                          autoComplete={invitation ? 'new-password' : 'current-password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={invitation ? 12 : 1}
                        />
                      </div>
                    </label>
                  )}
                  {config?.oidc && (
                    <>
                      <a className="primary full" href={`/v1/auth/oidc?locale=${getLocale()}`}>
                        {t('Se connecter à Vename Mail')}
                        <ArrowRight size={18} />
                      </a>
                      <details ref={ssoDetails} style={{ marginTop: 20 }}>
                        <summary>{t('Connexion Microsoft Entra ID de mon entreprise')}</summary>
                        <label>
                          {t('Domaine ou adresse professionnelle')}
                          <input
                            ref={ssoInput}
                            type="text"
                            placeholder="entreprise.ci"
                            value={ssoDomain}
                            onChange={(event) => {
                              setSsoDomain(event.target.value);
                              setSsoError('');
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                startSso();
                              }
                            }}
                            aria-invalid={Boolean(ssoError)}
                            aria-describedby={ssoError ? 'sso-field-error' : 'sso-field-help'}
                          />
                        </label>
                        <small id="sso-field-help" className="sso-field-help">
                          {t('Exemple : entreprise.ci ou nom@entreprise.ci.')}
                        </small>
                        {ssoErrorText && (
                          <p id="sso-field-error" className="sso-field-error" role="alert">
                            {ssoErrorText}
                          </p>
                        )}
                        <button type="button" className="secondary full" onClick={startSso}>
                          {t('Continuer avec Microsoft')}
                        </button>
                      </details>
                      <details style={{ marginTop: 20 }}>
                        <summary>{t('Mot de passe oublié ou téléphone perdu ?')}</summary>
                        <p className="muted">
                          {t(
                            t(
                              'Sur l’écran de connexion sécurisée, choisissez « Mot de passe oublié » pour recevoir un lien sur votre adresse de récupération extérieure à Vename Mail. Votre second facteur restera requis.',
                            ),
                          )}
                        </p>
                        <p className="muted">
                          {t(
                            t(
                              'Si votre téléphone est indisponible, choisissez une autre méthode et utilisez un code de secours conservé lors de l’activation. Si vous avez également perdu vos codes, contactez votre administrateur pour une vérification d’identité.',
                            ),
                          )}
                        </p>
                        <p className="muted">
                          <a href="mailto:support@vename.com">
                            {t('Écrire au support Vename depuis une autre adresse email')}
                          </a>
                        </p>
                      </details>
                    </>
                  )}
                </>
              ) : (
                <>
                  {session?.enrollmentRequired &&
                    (setup ? (
                      <div className="mfa-setup">
                        <img
                          src={setup.qrCode}
                          alt="QR code de configuration MFA"
                          width="180"
                          height="180"
                        />
                        <details>
                          <summary>{t('Saisir la clé manuellement')}</summary>
                          <code className="break-word">{setup.secret}</code>
                        </details>
                      </div>
                    ) : (
                      <Loading label={t('Préparation du second facteur…')} />
                    ))}
                  <label>
                    {t('Code de vérification')}
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="000 000"
                      autoComplete="one-time-code"
                      autoFocus
                      className="code-input"
                      required
                      minLength={6}
                      maxLength={64}
                    />
                  </label>
                </>
              )}
              {!(stage === 'login' && config?.oidc) && (
                <button className="primary full" type="submit" disabled={busy}>
                  {busy
                    ? t('Vérification…')
                    : stage === 'login'
                      ? t('Se connecter')
                      : t('Vérifier mon code')}
                  <ArrowRight size={18} />
                </button>
              )}
            </form>
          )}
          <div className="login-security">
            <ShieldCheck size={17} />
            <span>{t('Votre accès est protégé par une double authentification.')}</span>
          </div>
          {config?.mode === 'local' && stage === 'login' && (
            <div className="local-note">
              <span className="status-dot" />
              Environnement local · Données fictives
              {!config.oidc && (
                <small>
                  {t('Identifiants disponibles dans')} <code>.data/bootstrap.json</code>
                </small>
              )}
            </div>
          )}
        </div>
        <footer className="login-footer">
          Vename Mail © {new Date().getFullYear()}
          <span>{t('Votre messagerie, simplement.')}</span>
        </footer>
      </main>
    </div>
  );
}
